import { AcademicTitleEnum, SalutationEnum } from '@app/icare/db/schema/common'
import { t } from '@lingui/core/macro'
import { Q, QueryBaseSchema } from '@pkg/api/query'
import { defineEntityConfig } from '@pkg/db/schema/EntityConfig'
import { formatAddress, mergeNonEmptyStrings } from '@pkg/shared/utils'
import dayjs from 'dayjs'
import { FaUser } from 'react-icons/fa6'
import { z } from 'zod'
import { type Patient, PatientSchema, PatientStatusEnum } from './schema/Patient'

/**
 * Options for formatting patient title
 */
interface FormatOpts {
  lastNameFirst?: boolean
  includeSalutation?: boolean
}

/**
 * Format patient title with salutation, title, name, and birthdate
 * Moved from PatientTitle.tsx to be part of the entity configuration
 *
 * @example
 * formatPatientTitle(patient) // "Herr Dr. Max Mustermann, 01.01.1980"
 * formatPatientTitle(patient, { lastNameFirst: true }) // "Herr Dr. Mustermann, Max, 01.01.1980"
 */
export const formatPatientTitle = (patient: Patient, opts?: FormatOpts) => {
  const { lastNameFirst = false, includeSalutation = true } = opts ?? {}
  const birthdate = patient?.personal?.birthdate ? dayjs(patient.personal.birthdate) : undefined

  const name = mergeNonEmptyStrings(
    [
      includeSalutation ? SalutationEnum.$.getLabel(patient?.personal?.salutation) : undefined,
      AcademicTitleEnum.$.getLabel(patient?.personal?.honorificPrefix),
      lastNameFirst
        ? mergeNonEmptyStrings([patient?.personal?.lastName, patient?.personal?.firstName], ', ')
        : mergeNonEmptyStrings([patient?.personal?.firstName, patient?.personal?.lastName]),
    ],
    ' ',
  )

  if (birthdate?.isValid()) {
    return `${name}, ${birthdate.format('DD.MM.YYYY')}`
  }
  return name
}

export const PatientFilterSchema = QueryBaseSchema.extend({
  sortBy: z.enum(['name', 'status.value', 'createdAt']).nullish(),
  filters: z
    .object({
      status: Q.in(z.enum(PatientStatusEnum.$.zodEnum())).nullish(),
    })
    .optional(),
})

export const patientConfig = defineEntityConfig(() => ({
  schema: PatientSchema,

  label: t`Patient:in`,
  labelPlural: t`Patient:innen`,

  icon: FaUser,

  route: {
    list: '/patienten',
    edit: '/patienten/$id',
  },

  relationSelectionColumns: [
    {
      title: t`Name`,
      key: 'name',
      render: (_, record) => record.personal?.lastName,
    },
    {
      title: t`Vorname`,
      key: 'firstName',
      render: (_, record) => record.personal?.firstName,
    },
    {
      title: t`Adresse`,
      key: 'address',
      render: (_, record) => formatAddress([], record.address),
    },
  ],

  indexColumns: [
    {
      title: t`Name`,
      key: 'name',
      render: (_, record) => record.personal?.lastName,
    },
    {
      title: t`Vorname`,
      key: 'firstName',
      render: (_, record) => record.personal?.firstName,
    },
    {
      title: t`Adresse`,
      key: 'address',
      render: (_, record) => formatAddress([], record.address),
    },
  ],

  search: {
    filterSchema: PatientFilterSchema,
    sortableFields: ['personal.lastName', 'createdAt', 'status.value'] as const,
    defaultSortBy: 'personal.lastName',
  },

  format: {
    primary: val => (val ? formatPatientTitle(val) : undefined),
    secondary: val => (val ? formatAddress([], val.address) : undefined),
  },
}))
